"use client";

import React, { useState, useEffect, useMemo, useRef } from "react";
import { Search, RotateCcw, Filter, BookOpen, FileText, ChevronDown } from "lucide-react";
import { syllabusData } from "@/data/syllabus-data";
import CustomSelect from "@/components/ui/CustomSelect";

const EXAM_TYPES = [
  { label: "All Exams", value: "ALL" },
  { label: "JEE Main", value: "JEE_MAIN" },
  { label: "JEE Advanced", value: "JEE_ADVANCED" },
];

const DIFFICULTIES = [
  { label: "All Difficulties", value: "ALL" },
  { label: "Easy", value: "Easy" },
  { label: "Medium", value: "Medium" },
  { label: "Hard", value: "Hard" },
];

const QUESTION_TYPES = [
  { label: "All Formats", value: "ALL" },
  { label: "Single Correct (MCQ)", value: "MCQ" },
  { label: "Numerical Value", value: "NUMERIC" },
  { label: "Multi-Correct", value: "MULTI_CORRECT" },
  { label: "Comprehension / Passage", value: "COMPREHENSION" },
];

function YearFilterPills({
  availableYears = [],
  selectedYear = "All",
  onSelectYear,
  activeClass = "bg-orange-600 text-white shadow-sm",
  inactiveClass = "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700",
  size = "sm",
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen]);

  const yearsOnly = useMemo(() => {
    return availableYears.filter((y) => y !== "All" && y !== "ALL");
  }, [availableYears]);

  // If 4 or fewer years, show all as pills without dropdown.
  // If more than 4 years, display top 3 recent years as quick pills and fold the rest into "More ▾"
  const hasManyYears = yearsOnly.length > 4;
  const recentYears = hasManyYears ? yearsOnly.slice(0, 3) : yearsOnly;
  const olderYears = hasManyYears ? yearsOnly.slice(3) : [];

  const isOlderSelected = hasManyYears && olderYears.includes(selectedYear);
  const pillPadding = size === "md" ? "px-3.5 py-2" : "px-3.5 py-1.5";

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {/* "All" Pill */}
      <button
        type="button"
        onClick={() => onSelectYear("All")}
        className={`${pillPadding} rounded-lg text-xs font-bold transition-all cursor-pointer ${
          selectedYear === "All" || selectedYear === "ALL"
            ? activeClass
            : inactiveClass
        }`}
      >
        All
      </button>

      {/* Quick Pills for Recent Years */}
      {recentYears.map((y) => (
        <button
          key={y}
          type="button"
          onClick={() => onSelectYear(y)}
          className={`${pillPadding} rounded-lg text-xs font-bold transition-all cursor-pointer ${
            selectedYear === y
              ? activeClass
              : inactiveClass
          }`}
        >
          {y}
        </button>
      ))}

      {/* Dropdown for older archive years */}
      {hasManyYears && (
        <div className="relative inline-block" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className={`flex items-center gap-1 ${pillPadding} rounded-lg text-xs font-bold transition-all cursor-pointer ${
              isOlderSelected
                ? activeClass
                : inactiveClass
            }`}
          >
            <span>{isOlderSelected ? selectedYear : "More"}</span>
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
            />
          </button>

          {isOpen && (
            <div className="absolute right-0 sm:left-0 sm:right-auto mt-1.5 w-48 bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-800 p-2 z-50 animate-in fade-in zoom-in-95 duration-150">
              <div className="px-2 py-1 mb-1 text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 border-b border-gray-100 dark:border-gray-800">
                Older Archives
              </div>
              <div className="max-h-48 overflow-y-auto grid grid-cols-2 gap-1 p-0.5 custom-scrollbar">
                {olderYears.map((y) => (
                  <button
                    key={y}
                    type="button"
                    onClick={() => {
                      onSelectYear(y);
                      setIsOpen(false);
                    }}
                    className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold text-center transition-all cursor-pointer ${
                      selectedYear === y
                        ? "bg-orange-600 text-white font-bold shadow-sm"
                        : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                    }`}
                  >
                    {y}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function PYQFilterBar({
  activeTab,
  setActiveTab,
  filters,
  onFilterChange,
  onResetFilters,
  totalCount,
  loading,
  availableYears = ["All", "2026", "2025", "2024"],
}) {
  const [searchInput, setSearchInput] = useState(filters.search || "");
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        onFilterChange("search", searchInput);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput, filters.search, onFilterChange]);

  // Sync external search changes during render
  const [prevSearch, setPrevSearch] = useState(filters.search || "");
  if (prevSearch !== (filters.search || "")) {
    setPrevSearch(filters.search || "");
    setSearchInput(filters.search || "");
  }

  // Extract chapters based on current subject
  const chapterOptions = useMemo(() => {
    let list = [];
    if (!filters.subject || filters.subject === "ALL") {
      const allChaps = [
        ...(syllabusData.physics?.chapters || []),
        ...(syllabusData.chemistry?.chapters || []),
        ...(syllabusData.mathematics?.chapters || []),
      ];
      list = Array.from(new Set(allChaps.map((c) => c.name))).sort();
    } else {
      const subKey = filters.subject.toLowerCase();
      const chaps = syllabusData[subKey]?.chapters || [];
      list = chaps.map((c) => c.name);
    }

    return [
      { label: `All Chapters (${filters.subject === "ALL" ? "All Subjects" : filters.subject})`, value: "ALL" },
      ...list.map((c) => ({ label: c, value: c })),
    ];
  }, [filters.subject]);

  const hasActiveFilters =
    filters.subject !== "ALL" ||
    filters.chapter !== "ALL" ||
    filters.examType !== "ALL" ||
    filters.year !== "All" ||
    filters.difficulty !== "ALL" ||
    filters.questionType !== "ALL" ||
    filters.search !== "" ||
    !!filters.tag;

  return (
    <div className="relative z-20 bg-white dark:bg-gray-900 rounded-3xl shadow-lg shadow-slate-200/50 dark:shadow-[0_4px_20px_rgba(0,0,0,0.4)] border border-slate-200 dark:border-gray-800 ring-1 ring-black/[0.03] dark:ring-white/[0.06] p-4 md:p-6 mb-8 transition-all">
      {/* Top Mode Switcher & Counter */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pb-5 border-b border-gray-100 dark:border-gray-800">
        <div className="flex p-1 bg-gray-100 dark:bg-gray-800/90 rounded-xl w-full sm:w-auto">
          <button
            type="button"
            onClick={() => setActiveTab("practice")}
            className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold transition-all cursor-pointer ${
              activeTab === "practice"
                ? "bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-md"
                : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            }`}
          >
            <BookOpen className="w-4 h-4" />
            Chapter-Wise Practice
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("papers")}
            className={`flex-1 sm:flex-none flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg text-sm font-bold transition-all cursor-pointer ${
              activeTab === "papers"
                ? "bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-md"
                : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            }`}
          >
            <FileText className="w-4 h-4" />
            Full Shift Papers
          </button>
        </div>

        {/* Counter Badge */}
        <div className="text-xs sm:text-sm font-medium">
          {loading ? (
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-gray-100 dark:bg-gray-800/60 animate-pulse">
              <span className="w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-gray-700" />
              <span className="w-24 h-3.5 bg-gray-200 dark:bg-gray-700 rounded" />
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl bg-gray-100/90 dark:bg-gray-800/70 border border-gray-200/70 dark:border-gray-800/60 text-gray-600 dark:text-gray-400">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0" />
              <span>
                Found{" "}
                <strong className="font-bold text-gray-900 dark:text-white tabular-nums">
                  {Number(totalCount || 0).toLocaleString("en-IN")}
                </strong>{" "}
                {activeTab === "practice"
                  ? totalCount === 1
                    ? "Question"
                    : "Questions"
                  : totalCount === 1
                    ? "Paper"
                    : "Papers"}
              </span>
            </div>
          )}
        </div>
      </div>

      {activeTab === "practice" && (
        <div className="pt-5 space-y-4">
          {/* Subject Pills (Segmented control container with inner padding to prevent boundary clipping) */}
          <div className="flex items-center gap-1.5 p-1 bg-gray-100 dark:bg-gray-800/70 rounded-2xl overflow-x-auto scrollbar-hide max-w-fit">
            {[
              { label: "All Subjects", value: "ALL", activeBg: "bg-gray-900 dark:bg-white text-white dark:text-gray-900 shadow-sm" },
              { label: "Physics", value: "Physics", activeBg: "bg-blue-600 text-white shadow-sm" },
              { label: "Chemistry", value: "Chemistry", activeBg: "bg-emerald-600 text-white shadow-sm" },
              { label: "Mathematics", value: "Mathematics", activeBg: "bg-purple-600 text-white shadow-sm" },
            ].map((sub) => {
              const isSelected = filters.subject === sub.value;
              return (
                <button
                  key={sub.value}
                  type="button"
                  onClick={() => onFilterChange("subject", sub.value)}
                  className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all whitespace-nowrap cursor-pointer ${
                    isSelected
                      ? sub.activeBg
                      : "text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200/60 dark:hover:bg-gray-700/50"
                  }`}
                >
                  {sub.label}
                </button>
              );
            })}
          </div>

          {/* Search Box and Custom Chapter Dropdown */}
          <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
            {/* Search Box */}
            <div className="md:col-span-6 relative group">
              <Search className="w-4 h-4 text-gray-400 group-focus-within:text-orange-500 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none transition-colors" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search topics, questions, or formulas..."
                className="w-full pl-10 pr-9 py-2.5 rounded-xl text-sm bg-gray-50 dark:bg-gray-800/80 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 text-gray-900 dark:text-white placeholder-gray-400 outline-none focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500 ring-offset-transparent transition-colors"
              />
              {searchInput && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchInput("");
                    onFilterChange("search", "");
                  }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-md transition-colors cursor-pointer"
                  aria-label="Clear search"
                >
                  <span className="text-sm font-bold leading-none block">×</span>
                </button>
              )}
            </div>

            {/* Custom Chapter Select with search filter inside */}
            <div className="md:col-span-6">
              <CustomSelect
                value={filters.chapter}
                onChange={(val) => onFilterChange("chapter", val)}
                options={chapterOptions}
                placeholder="Select Chapter"
                searchable={true}
                searchPlaceholder="Filter chapters..."
              />
            </div>
          </div>

          {/* Dynamic Year Pills & Filter Actions */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs pt-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-semibold text-gray-500 dark:text-gray-400 shrink-0 mr-1">Year:</span>
              <YearFilterPills
                availableYears={availableYears}
                selectedYear={filters.year}
                onSelectYear={(y) => onFilterChange("year", y)}
                activeClass="bg-orange-600 text-white shadow-sm"
                size="sm"
              />
            </div>

            {/* Actions: More Filters + Active Tag + Reset */}
            <div className="flex items-center gap-2 self-end sm:self-auto shrink-0 flex-wrap">
              {/* Active concept tag pill */}
              {filters.tag && (
                <span className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-orange-100 dark:bg-orange-950/40 text-orange-700 dark:text-orange-300 border border-orange-200 dark:border-orange-800">
                  <span># {filters.tag}</span>
                  <button
                    type="button"
                    onClick={() => onFilterChange("tag", "")}
                    className="ml-0.5 text-orange-500 hover:text-orange-700 dark:hover:text-orange-200 cursor-pointer leading-none"
                    aria-label="Remove tag filter"
                  >
                    ×
                  </button>
                </span>
              )}

              <button
                type="button"
                onClick={() => setIsAdvancedOpen(!isAdvancedOpen)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-950/30 transition-all cursor-pointer shrink-0"
              >
                <Filter className="w-3.5 h-3.5" />
                {isAdvancedOpen ? "Hide Filters" : "More Filters"}
              </button>

              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={onResetFilters}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-950/30 transition-all cursor-pointer shrink-0"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  Reset
                </button>
              )}
            </div>
          </div>

          {/* Collapsible Advanced Filters with CustomSelects */}
          {isAdvancedOpen && (
            <div className="pt-4 border-t border-gray-100 dark:border-gray-800 grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Exam Type */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5">
                  Exam Type
                </label>
                <CustomSelect
                  value={filters.examType}
                  onChange={(val) => onFilterChange("examType", val)}
                  options={EXAM_TYPES}
                />
              </div>

              {/* Difficulty */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5">
                  Difficulty Level
                </label>
                <CustomSelect
                  value={filters.difficulty}
                  onChange={(val) => onFilterChange("difficulty", val)}
                  options={DIFFICULTIES}
                />
              </div>

              {/* Question Type */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 mb-1.5">
                  Question Format
                </label>
                <CustomSelect
                  value={filters.questionType}
                  onChange={(val) => onFilterChange("questionType", val)}
                  options={QUESTION_TYPES}
                />
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === "papers" && (
        <div className="pt-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          {/* Exam Type for Papers */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {EXAM_TYPES.map((et) => (
              <button
                key={et.value}
                type="button"
                onClick={() => onFilterChange("examType", et.value)}
                className={`px-4 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                  filters.examType === et.value
                    ? "bg-orange-600 text-white shadow-md"
                    : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700"
                }`}
              >
                {et.label}
              </button>
            ))}
          </div>

          {/* Dynamic Year pills for Papers */}
          <YearFilterPills
            availableYears={availableYears}
            selectedYear={filters.year}
            onSelectYear={(y) => onFilterChange("year", y)}
            activeClass="bg-gray-900 dark:bg-white text-white dark:text-gray-900 shadow-md"
            size="md"
          />
        </div>
      )}
    </div>
  );
}